// Source of truth: platform-docs/legal/PRIVACY_POLICY.md
// When publishing a new privacy-policy version, mirror the content changes here.
// The .md file is the legally canonical version.

import React from 'react';
import { Link } from 'react-router-dom';
import './Legal.css';

const PrivacyPolicy = () => (
  <div className="legal-page">
    <Link to="/login" className="legal-back">&larr; Back</Link>

    <h1>Privacy Policy</h1>
    <div className="legal-meta">
      <div><strong>Operator:</strong> Maia AI, LLC</div>
      <div><strong>Version:</strong> v1.0</div>
      <div><strong>Effective date:</strong> 2026-04-27</div>
      <div><strong>Last updated:</strong> 2026-04-27</div>
    </div>

    <hr />

    <h2>1. Overview</h2>
    <p>
      This Privacy Policy explains how <strong>Maia AI, LLC</strong> ("<strong>Maia</strong>," "<strong>we</strong>," "<strong>our</strong>," or "<strong>us</strong>") collects, uses, shares, retains, and protects information when you use the Maia service. This policy is a companion to the <Link to="/legal/terms">Terms of Use</Link> and is incorporated into them by reference.
    </p>
    <p>
      The service is an AI-assisted phone agent. Many of the privacy questions that matter most to users — what happens to my account number, my PIN, the audio of my call — are addressed in <strong>Section 4 (How we protect sensitive information)</strong> below. If you only read one section, read that one.
    </p>

    <hr />

    <h2>2. Information we collect</h2>
    <p>We collect three categories of information.</p>

    <h3>2.1 Information you give us</h3>
    <ul>
      <li><strong>Account information</strong> — your name, email address, password, and phone number, provided when you register.</li>
      <li><strong>Verification information</strong> — the phone number you verify as the caller-ID number Maia uses on outbound calls placed on your behalf.</li>
      <li><strong>Contacts</strong> — the people, businesses, account numbers, PINs, login credentials, and free-text notes you configure for Maia to use when navigating IVR systems on your behalf.</li>
      <li><strong>Instructions</strong> — the per-call goals, scripts, and context you give Maia so that it can place a specific call.</li>
      <li><strong>Communications</strong> — messages you send to us via support@maiatech.ai or other support channels.</li>
    </ul>

    <h3>2.2 Information we generate from your use of the service</h3>
    <ul>
      <li><strong>Call audio.</strong> Audio of phone calls placed or received through the service. The service uses real-time speech-to-text to transcribe both sides of the conversation.</li>
      <li><strong>Call transcripts.</strong> Text transcripts of calls, including Maia's speech, the recipient's speech (live agent or IVR), and any DTMF (touch-tone) digits entered.</li>
      <li><strong>Call summaries.</strong> AI-generated written summaries of what happened on a call.</li>
      <li><strong>Call metadata.</strong> Caller-ID, recipient number, timestamps, duration, call outcome, and technical status events from telephony providers.</li>
      <li><strong>AI model interactions.</strong> The prompts the service sends to and the responses it receives from the underlying AI model during a call. Sensitive values from your record are tokenized before they reach the AI model, as described in Section 4.</li>
      <li><strong>Usage logs.</strong> Which features you use, when, and from what device.</li>
    </ul>

    <h3>2.3 Information we receive from third parties</h3>
    <ul>
      <li><strong>Telephony providers</strong> (such as Twilio) supply call status, carrier information, and technical signaling related to your calls.</li>
      <li><strong>Authentication providers</strong> (Firebase Authentication) supply the identity-verification result when you log in.</li>
      <li><strong>Push-notification providers</strong> supply delivery confirmations for notifications we send to your device.</li>
    </ul>

    <h3>2.4 Information we do not collect</h3>
    <p>
      We do not access your device's microphone outside of an active call. We do not read your phone's contacts or photos. We do not track your location. We do not place advertising cookies. The mobile app, when launched, will request only the operating-system permissions strictly required to place calls and to send push notifications you have opted into.
    </p>

    <hr />

    <h2>3. How we use your information</h2>
    <p>We use your information for the following purposes:</p>
    <ul>
      <li><strong>To provide the service.</strong> Place calls on your behalf, route audio, transcribe and summarize, deliver notifications, hand control of the call to you when needed, and show you the call history afterwards.</li>
      <li><strong>To improve the service.</strong> When you explicitly flag a call through the in-app "flag this call" flow, we use a redacted copy of that call's transcript as a research input to improve the AI agent. This is a per-call, consent-bounded flow described in detail in the in-app consent text at the moment you flag the call.</li>
      <li><strong>To communicate with you.</strong> Service announcements, security alerts, billing notices, and responses to your support requests.</li>
      <li><strong>To protect the service and our users.</strong> Investigate and respond to suspected violations of the Terms of Use, fraud, abuse, or threats to safety. The scope of this access is broader than the consent-bounded research flow above and is set out in <strong>Section 6 of the <Link to="/legal/terms">Terms of Use</Link></strong>.</li>
      <li><strong>To comply with legal obligations.</strong> Comply with applicable law, respond to lawful requests from authorities, and enforce our agreements.</li>
    </ul>

    <hr />

    <h2>4. How we protect sensitive information</h2>
    <p>
      The service is engineered so that the values most users worry about — account numbers, Social Security numbers, PINs, dates of birth, login passwords — are not exposed to the AI components that drive the conversation.
    </p>

    <h3>4.1 Tokenization before the AI</h3>
    <p>
      Before any session reaches the AI model, sensitive values from your contact records are replaced with presence flags. The AI sees <em>that</em> you have a particular value (so it can plan around it), not <em>what the value is</em>.
    </p>

    <h3>4.2 Secure spoken-data entry</h3>
    <p>
      When an IVR asks Maia to speak a sensitive value (for example, a date of birth), the service looks the value up from your encrypted record at the moment of speaking and emits the audio directly. The value never enters the AI's prompt or response context.
    </p>

    <h3>4.3 Touch-tone (DTMF) entry path</h3>
    <p>
      When an IVR asks for a sensitive value via touch-tone digits, the service transmits the digits as DTMF tones directly. The digits are not sent to the AI model.
    </p>

    <h3>4.4 Output filter</h3>
    <p>
      A runtime filter scans every utterance the service is about to speak and blocks any string that matches a sensitive value from your record. This is a defense-in-depth safeguard against the AI ever speaking a value it should not have access to.
    </p>

    <h3>4.5 Redaction on storage and logs</h3>
    <p>
      Before any transcript or log entry is written to our backend, sensitive values are replaced with typed placeholders (for example, <code>&lt;ACCOUNT_NUMBER&gt;</code>, <code>&lt;PIN&gt;</code>). The redactor is designed to fail closed: if it cannot fully redact, the record is not written.
    </p>

    <h3>4.6 What this means in plain language</h3>
    <ul>
      <li>Your account numbers, PINs, login passwords, and SSN are not sent to the AI service in plaintext.</li>
      <li>They are stored on our backend in Firestore, encrypted at rest using Google-managed encryption keys, and used by the service to play into IVR systems on your behalf.</li>
      <li>They are not stored in our log files in plaintext.</li>
      <li>This protection is stronger than what most consumer apps offer, but no system is absolute. Section 6 of the <Link to="/legal/terms">Terms of Use</Link> describes the access right we reserve for service-protection investigations, and Section 6 below describes our retention windows.</li>
    </ul>

    <hr />

    <h2>5. How we share your information</h2>
    <p>
      We share your information only in the categories described below. <strong>We do not sell your personal information.</strong> We do not share your personal information for cross-context behavioral advertising.
    </p>

    <h3>5.1 Service providers</h3>
    <p>We use third-party providers to run the service. The principal providers, and what we share with each, are:</p>
    <ul>
      <li><strong>Twilio</strong> (telephony) — call audio, recipient phone numbers, caller-ID, and call-control signaling. Required to place phone calls.</li>
      <li><strong>Deepgram</strong> (speech-to-text) — call audio. Required to transcribe what is said on each call.</li>
      <li><strong>Anthropic</strong> (AI model) — the prompts and contextual information the service sends during a call. Sensitive values are tokenized before they reach Anthropic, as described in Section 4.</li>
      <li><strong>Google</strong> (Firebase Authentication, Firestore, Cloud Run, Cloud Functions, Cloud Logging) — account information, contact records (encrypted at rest), call history, and operational logs.</li>
    </ul>
    <p>
      Each provider processes your information under its own terms and privacy policy. We choose providers based on their security, reliability, and privacy posture, and we are working toward zero-retention contracts with our AI and speech-to-text providers so that no copy of your call content is retained on their side beyond the call itself.
    </p>

    <h3>5.2 Legal disclosures</h3>
    <p>
      We may disclose your information to law enforcement, regulators, courts, or other parties when we believe in good faith that disclosure is required by law, necessary to enforce the Terms of Use, necessary to protect the rights, property, or safety of Maia, our users, or others, or necessary in connection with an investigation of suspected misconduct.
    </p>

    <h3>5.3 Corporate transactions</h3>
    <p>
      If Maia is involved in a merger, acquisition, financing, reorganization, bankruptcy, or sale of assets, your information may be transferred as part of that transaction. We will provide notice, and any successor entity will be bound by this Privacy Policy or by one offering equivalent protection.
    </p>

    <h3>5.4 With your consent</h3>
    <p>
      We may share information in any other manner that we describe to you and to which you consent. The "flag this call" research flow described in Section 3 is one example.
    </p>

    <hr />

    <h2>6. Retention and deletion</h2>
    <p>We retain different categories of information for different periods.</p>
    <table>
      <thead>
        <tr>
          <th>Category</th>
          <th>Retention</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Account information (name, email, phone number, contacts)</td>
          <td>Until you delete your account, plus a short period for legal and operational reasons</td>
        </tr>
        <tr>
          <td>Call audio</td>
          <td>Not retained beyond the duration of the call (transcripts are retained instead)</td>
        </tr>
        <tr>
          <td>Call transcripts and summaries</td>
          <td><strong>30 days</strong>, then auto-deleted via Firestore time-to-live policy</td>
        </tr>
        <tr>
          <td>Flagged calls (the redacted transcripts you explicitly flagged for product improvement)</td>
          <td><strong>30 days</strong>, then auto-deleted</td>
        </tr>
        <tr>
          <td>Operational logs</td>
          <td>Up to 30 days for runtime logs; longer for security audit records as required for compliance</td>
        </tr>
        <tr>
          <td>Records subject to a legal hold or active investigation</td>
          <td>Held for the duration of the hold or investigation</td>
        </tr>
      </tbody>
    </table>
    <p>
      When you delete your account, we delete or de-identify your account information, contacts, and call history. Some data may persist for up to 30 days in backups before being purged.
    </p>

    <hr />

    <h2>7. Your choices and rights</h2>

    <h3>7.1 Access and correction</h3>
    <p>
      You can view and edit most of your information directly in the Maia app — your name, email, phone number, contacts, and call history. For information you cannot edit directly, write to support@maiatech.ai.
    </p>

    <h3>7.2 Account deletion</h3>
    <p>
      You can delete your account from the app's settings. Deletion is processed within 30 days. After processing, your call history, contacts, and account record are removed from our active systems.
    </p>

    <h3>7.3 Communication preferences</h3>
    <p>
      You can opt out of non-essential email communications through links in those emails. You cannot opt out of essential service communications (security alerts, billing notices, legally required notices) for as long as you have an account.
    </p>

    <h3>7.4 Push notifications</h3>
    <p>
      You can disable push notifications at the operating-system level on your device or in the Maia app's settings. This will not affect your ability to use the service.
    </p>

    <h3>7.5 State-specific rights</h3>
    <p>
      Residents of certain US states have additional rights under state privacy laws (including California's CCPA/CPRA, Texas's Data Privacy and Security Act, and similar laws in other states). These rights typically include the right to access the personal information we hold about you, to request its correction, to request its deletion, and to opt out of any sale or sharing of personal information for cross-context behavioral advertising.
    </p>
    <p>
      We do not sell or share personal information for cross-context behavioral advertising. To exercise the other rights, contact us at support@maiatech.ai. We will respond within the timeframe required by applicable law.
    </p>

    <h3>7.6 International users</h3>
    <p>
      The service is operated from the United States and is intended primarily for users in the United States. If you access the service from outside the United States, your information will be transferred to and processed in the United States. If GDPR or UK GDPR applies to you, you have rights of access, rectification, erasure, restriction, portability, and objection, and you may lodge a complaint with your local supervisory authority.
    </p>

    <hr />

    <h2>8. Security</h2>
    <p>We protect your information using a combination of technical and organizational safeguards:</p>
    <ul>
      <li><strong>Encryption in transit</strong> — all communication between your device and our service uses TLS.</li>
      <li><strong>Encryption at rest</strong> — Firestore and Cloud Storage encrypt data at rest using Google-managed keys.</li>
      <li><strong>Tokenization</strong> — sensitive values are tokenized before reaching the AI model (see Section 4).</li>
      <li><strong>Access controls</strong> — production credentials are stored in Google Secret Manager, not in source code or environment files. Service accounts use Application Default Credentials with least-privilege scopes.</li>
      <li><strong>Audit logging</strong> — administrative access is logged.</li>
      <li><strong>Vendor diligence</strong> — we contract only with providers that meet enterprise-grade security standards, and we maintain Data Processing Agreements where required.</li>
    </ul>
    <p>
      No system is perfectly secure. If you suspect your account has been compromised, contact us immediately at support@maiatech.ai.
    </p>

    <hr />

    <h2>9. Children</h2>
    <p>
      The service is not directed to children. The minimum age to use the service is <strong>18 years old</strong>, as set out in Section 3 of the <Link to="/legal/terms">Terms of Use</Link>. We do not knowingly collect information from anyone under 18. If we learn that we have collected information from a person under 18, we will delete it.
    </p>

    <hr />

    <h2>10. International transfers</h2>
    <p>
      We are based in the United States, and we process your information in the United States and in any other country where our service providers operate. By using the service, you understand that your information may be transferred to countries with different data-protection laws than the country in which you reside.
    </p>

    <hr />

    <h2>11. Changes to this policy</h2>
    <p>
      We may update this Privacy Policy. The current version is identified by the "Last updated" date in the header. For material changes, we will provide notice through the service or by email to the address registered to your account at least <strong>14 days</strong> before the change takes effect, matching the notice period in Section 9 of the <Link to="/legal/terms">Terms of Use</Link>.
    </p>
    <p>
      Continued use of the service on or after the effective date of an update constitutes acceptance of the updated policy.
    </p>

    <hr />

    <h2>12. Contact</h2>
    <p>
      Questions about this Privacy Policy, requests to exercise your rights, and privacy concerns should be sent to:
    </p>
    <p>
      <strong>Maia AI, LLC</strong><br />
      Email: support@maiatech.ai
    </p>
    <p>
      We will respond within the timeframe required by applicable law.
    </p>

    <p className="legal-end">End of Privacy Policy.</p>
  </div>
);

export default PrivacyPolicy;
